/**
 * @file components/dispatch/ApplicationDetailsFields.tsx
 * @description 案件「申請明細」表單欄位區塊（新建案件 / 修改案件共用，2026-09-13 回報序14）。
 *
 * - 有機 TOP：多組「驗證品項（作物字典下拉）＋驗證範圍（自由文字多行，序6）＋附件」，
 *   以及依驗證服務類型分展示的驗證面積（序165）。
 * - 產銷履歷 TAP：多行明細（作物管理選擇品項 / 作物產期 / 申請面積 / 合理採收量 / 附件）
 *   ＋生產地筆數＋場區筆數＋驗證總面積自動加總；整區非必填（序16）。
 *
 * 另導出 {@link confirmAndEnsureTopScopes}：提交前比對作物管理，對尚未建立的驗證範圍
 * 逐項提示「驗證範圍【X】尚未建立於【品項】，是否新增？」，確認後補建（序7）。
 *
 * 表單欄位名稱見 application-details-form.ts 的 ApplicationDetailsFormValues，
 * 使用方表單值型別需包含這些欄位。
 */

import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  type FormInstance,
} from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import type { EnsureTopCropScopesResult } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { message, modal } from '@/lib/antd-app';
import { AttachmentIdsUpload } from '@/components/dispatch/AttachmentIdsUpload';
import {
  buildTopItemOptionGroups,
  buildTopScopeOptions,
} from '@/components/dispatch/top-crop-options';
import {
  findMissingTopScopes,
  tapRowHasContent,
  tapTotalAreaOf,
  type MissingTopScope,
  type TapRowValue,
  type TopProductGroupValue,
} from '@/components/dispatch/application-details-form';
import type { Crop } from '@/hooks/useCrops';

interface Props {
  /** 使用方表單實例（欄位名稱需含 ApplicationDetailsFormValues） */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 新建/修改兩張表單值型別不同，僅共用本區塊欄位
  form: FormInstance<any>;
  /** 申請明細分流：TOP / TAP；undefined＝尚未決定（未選客戶） */
  detailKind: 'TOP' | 'TAP' | undefined;
  /** 作物字典（已依 detailKind 對應方案過濾的啟用項） */
  cropItems: readonly Crop[];
  /** 作物字典載入中 */
  cropsLoading?: boolean;
  /** 是否為追蹤 / 展延查驗（決定有機面積欄位分展示） */
  isTrackingArea: boolean;
}

/** 驗證範圍輸入框下方最多列出幾個作物管理既有範圍作參考 */
const SCOPE_HINT_LIMIT = 12;

/** 案件申請明細欄位區塊 */
export function ApplicationDetailsFields({
  form,
  detailKind,
  cropItems,
  cropsLoading,
  isTrackingArea,
}: Props) {
  const topItemOptionGroups = useMemo(() => buildTopItemOptionGroups(cropItems), [cropItems]);
  const tapCropOptions = useMemo(
    () =>
      cropItems.map((crop) => ({
        value: crop.id,
        label: `${crop.mainCategory}／${crop.subCategory}／${crop.name}`,
      })),
    [cropItems],
  );

  const topGroups = Form.useWatch('topProductGroups', form) as TopProductGroupValue[] | undefined;
  const tapRows = Form.useWatch('tapRows', form) as TapRowValue[] | undefined;
  const topOrganicAreaHa = Form.useWatch('topOrganicAreaHa', form) as number | undefined;
  const topOrganicConversionAreaHa = Form.useWatch('topOrganicConversionAreaHa', form) as
    | number
    | undefined;
  const tapTotalArea = useMemo(() => tapTotalAreaOf(tapRows), [tapRows]);
  const topTrackingTotalArea = (topOrganicAreaHa ?? 0) + (topOrganicConversionAreaHa ?? 0);

  /**
   * 驗證品項下拉選項：字典分組＋存量案件中對不上字典的品項原文（修改案件時避免下拉顯示空白）。
   */
  const itemSelectOptions = useMemo(() => {
    const known = new Set(topItemOptionGroups.flatMap((g) => g.options.map((o) => o.value)));
    const legacy = Array.from(
      new Set(
        (topGroups ?? []).map((g) => g?.itemKey).filter((k): k is string => !!k && !known.has(k)),
      ),
    ).map((k) => ({ value: k, label: `${k}（非作物管理項目）`, keywords: k }));
    return legacy.length
      ? [...topItemOptionGroups, { label: '既有案件資料', options: legacy }]
      : topItemOptionGroups;
  }, [topItemOptionGroups, topGroups]);

  if (!detailKind) {
    return (
      <span style={{ color: 'rgba(0,0,0,0.45)' }}>
        請先選擇客戶（申請明細結構由客戶的驗證方案決定）
      </span>
    );
  }

  if (detailKind === 'TOP') {
    return (
      <>
        {/* 序164/序6：一組「驗證品項（字典）＋驗證範圍（自由文字）」為一列，可新增多組 */}
        <Form.Item label="驗證品項／驗證範圍" style={{ marginBottom: 16 }}>
          <Form.List name="topProductGroups">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                {fields.map((field) => {
                  const itemKey = topGroups?.[field.name]?.itemKey;
                  const existingScopes = buildTopScopeOptions(cropItems, itemKey).map(
                    (o) => o.label,
                  );
                  return (
                    <div
                      key={field.key}
                      style={{
                        position: 'relative',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: '16px 16px 12px',
                        background: '#ffffff',
                      }}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<Trash2 size={14} />}
                        onClick={() => remove(field.name)}
                        aria-label="刪除此組品項/範圍"
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                        }}
                      />
                      <Form.Item
                        name={[field.name, 'itemKey']}
                        label="驗證品項"
                        style={{ marginBottom: 12 }}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="選擇驗證品項（作物管理）"
                          style={{ width: '100%', maxWidth: 360 }}
                          loading={cropsLoading}
                          options={itemSelectOptions}
                          // 依 label + keywords（所屬類別與旗下產品範圍）比對，
                          // 讓使用者輸入個別作物名（如「葉菜甘藷」）也能找到其品項「短期葉菜」
                          filterOption={(input, opt) =>
                            `${opt?.label ?? ''} ${(opt as { keywords?: string })?.keywords ?? ''}`
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'scopeText']}
                        label="驗證範圍"
                        style={{ marginBottom: 12 }}
                        extra={
                          itemKey && existingScopes.length ? (
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              作物管理已有：
                              {existingScopes.slice(0, SCOPE_HINT_LIMIT).join('、')}
                              {existingScopes.length > SCOPE_HINT_LIMIT
                                ? ` 等 ${existingScopes.length} 項`
                                : ''}
                            </Typography.Text>
                          ) : undefined
                        }
                      >
                        {/* 固定寬度：避免 autoSize 在 Space(wrap) 的內容寬度容器中以百分比寬度量測，
                            造成「量高 → 換寬 → 再量高」的無限重排 */}
                        <Input.TextArea
                          style={{ width: '100%' }}
                          rows={2}
                          maxLength={900}
                          placeholder="直接輸入核定之驗證範圍，多項以「、」或換行分隔，例：香蕉、芭蕉"
                        />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'attachmentFileIds']}
                        label="附件"
                        style={{ marginBottom: 0 }}
                      >
                        <AttachmentIdsUpload buttonText="附件" />
                      </Form.Item>
                    </div>
                  );
                })}
                <Button size="small" icon={<Plus size={14} />} onClick={() => add({})}>
                  增加欄位
                </Button>
              </Space>
            )}
          </Form.List>
        </Form.Item>

        {/* 序165：有機驗證面積依驗證服務類型分展示 */}
        {!isTrackingArea ? (
          <Form.Item name="topApplyAreaHa" label="申請驗證面積" style={{ marginBottom: 0 }}>
            <InputNumber
              min={0}
              max={999999}
              precision={4}
              style={{ width: 240 }}
              addonAfter="公頃"
              placeholder="0.0000"
            />
          </Form.Item>
        ) : (
          <Space size={24} align="center" wrap>
            <Form.Item name="topOrganicAreaHa" label="有機面積" style={{ marginBottom: 0 }}>
              <InputNumber
                min={0}
                max={999999}
                precision={4}
                style={{ width: 180 }}
                addonAfter="公頃"
              />
            </Form.Item>
            <Form.Item
              name="topOrganicConversionAreaHa"
              label="有機轉型期面積"
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                max={999999}
                precision={4}
                style={{ width: 180 }}
                addonAfter="公頃"
              />
            </Form.Item>
            <Typography.Text>
              驗證面積合計（自動加總）：
              <Typography.Text strong>{topTrackingTotalArea.toFixed(4)}</Typography.Text> 公頃
            </Typography.Text>
          </Space>
        )}
      </>
    );
  }

  // ─── TAP（序16：整區非必填；僅「填了其他欄位卻未選品項」時提示，避免該行被靜默略過） ───
  return (
    <>
      <Form.List name="tapRows">
        {(fields, { add, remove }) => (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {fields.map((field, idx) => (
              <div
                key={field.key}
                style={{
                  position: 'relative',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  padding: '16px 16px 12px',
                  background: '#ffffff',
                }}
              >
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<Trash2 size={14} />}
                  onClick={() => remove(field.name)}
                  aria-label={`刪除第 ${idx + 1} 行明細`}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                  }}
                />
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                  <Form.Item
                    name={[field.name, 'cropId']}
                    label="驗證品項"
                    rules={[
                      {
                        validator: (_, value: number | undefined) =>
                          value || !tapRowHasContent(tapRows?.[field.name])
                            ? Promise.resolve()
                            : Promise.reject(new Error('已填寫本行其他欄位，請選擇品項或刪除此行')),
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="選擇驗證品項（作物管理）"
                      style={{ width: '100%', maxWidth: 480 }}
                      loading={cropsLoading}
                      options={tapCropOptions}
                      optionFilterProp="label"
                      onChange={(cropId: number | undefined) => {
                        const crop = cropItems.find((item) => item.id === cropId);
                        const rows = [
                          ...((form.getFieldValue('tapRows') as TapRowValue[] | undefined) ?? []),
                        ];
                        rows[field.name] = {
                          ...rows[field.name],
                          cropId,
                          productCategory: crop?.subCategory,
                          productItem: crop?.name,
                        };
                        form.setFieldValue('tapRows', rows);
                      }}
                    />
                  </Form.Item>
                  {/* 文字快照隨 cropId 一起提交；後端仍會以啟用作物字典覆寫校正。 */}
                  <Form.Item name={[field.name, 'productCategory']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name={[field.name, 'productItem']} hidden>
                    <Input />
                  </Form.Item>
                  <Space wrap style={{ width: '100%' }}>
                    <Form.Item name={[field.name, 'cropPeriod']} label="作物產期" style={{ marginBottom: 0 }}>
                      <Input placeholder="作物產期" maxLength={100} style={{ width: 180 }} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'appliedArea']} label="申請面積" style={{ marginBottom: 0 }}>
                      <InputNumber
                        stringMode
                        min={0}
                        max={999999}
                        precision={4}
                        placeholder="申請面積"
                        style={{ width: 160 }}
                        addonAfter="公頃"
                      />
                    </Form.Item>
                    <Form.Item name={[field.name, 'reasonableYield']} label="合理採收量" style={{ marginBottom: 0 }}>
                      <Input placeholder="合理採收量" maxLength={100} style={{ width: 180 }} />
                    </Form.Item>
                  </Space>
                  <Form.Item name={[field.name, 'attachmentFileIds']} label="附件" style={{ marginBottom: 0 }}>
                    <AttachmentIdsUpload buttonText="附件" />
                  </Form.Item>
                </Space>
              </div>
            ))}
            <Button size="small" icon={<Plus size={14} />} onClick={() => add({})}>
              新增明細行
            </Button>
          </Space>
        )}
      </Form.List>

      {/* 序 56：生產地及場區筆數拆為兩欄，排序 生產地筆數 → 場區筆數 → 驗證總面積 */}
      <Space size={24} align="center" wrap style={{ marginTop: 16 }}>
        <Form.Item name="tapLandCount" label="生產地筆數" style={{ marginBottom: 0 }}>
          <InputNumber min={0} max={99999} precision={0} style={{ width: 140 }} addonAfter="筆" />
        </Form.Item>
        <Form.Item name="tapSiteCount" label="場區筆數" style={{ marginBottom: 0 }}>
          <InputNumber min={0} max={99999} precision={0} style={{ width: 140 }} addonAfter="筆" />
        </Form.Item>
        <Typography.Text>
          驗證總面積（自動加總）：<Typography.Text strong>{tapTotalArea}</Typography.Text> 公頃
        </Typography.Text>
      </Space>
    </>
  );
}

// ─── 序7：提交前比對作物管理並確認補建驗證範圍 ─────────────────────────────────

/** 確認彈窗內容：逐項列出尚未建立的範圍，預設全勾（勾選＝是、取消勾選＝否） */
function MissingScopesChecklist({
  missing,
  selection,
}: {
  missing: MissingTopScope[];
  /** 可變容器：彈窗 onOk 時讀取當下勾選結果 */
  selection: { keys: string[] };
}) {
  const [keys, setKeys] = useState<string[]>(selection.keys);
  return (
    <div>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        以下驗證範圍尚未建立於作物管理。勾選者將自動新增至【作物管理】對應的驗證品項下；
        未勾選者不新增，維持原作物管理資料（不影響本案件儲存）。
      </Typography.Paragraph>
      <Checkbox.Group
        value={keys}
        onChange={(next) => {
          setKeys(next);
          selection.keys = next;
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
      >
        {missing.map((m) => {
          const key = missingKey(m);
          return (
            <Checkbox key={key} value={key}>
              驗證範圍【{m.name}】尚未建立於【{m.subCategory}】，是否新增？
            </Checkbox>
          );
        })}
      </Checkbox.Group>
    </div>
  );
}

/** 缺漏項唯一鍵 */
function missingKey(m: MissingTopScope): string {
  return `${m.mainCategory}|${m.subCategory}|${m.name}`;
}

/**
 * 提交案件前的序7流程：比對作物管理，若有尚未建立的驗證範圍則彈窗逐項確認，
 * 對勾選「是」的項目呼叫 POST /api/settings/crops/ensure-top-scopes 補建。
 *
 * 不論使用者選擇新增與否、或補建失敗，皆 resolve 讓案件照常儲存
 * （作物管理補建是附帶動作，不應阻斷派案）。
 *
 * @param groups 表單中的驗證品項／範圍分組
 * @param cropItems TOP 作物字典（啟用項）
 * @param onCropsChanged 補建成功後的回呼（通常用於 invalidate 作物字典快取）
 */
export async function confirmAndEnsureTopScopes(
  groups: readonly TopProductGroupValue[] | undefined,
  cropItems: readonly Crop[],
  onCropsChanged?: () => void,
): Promise<void> {
  const missing = findMissingTopScopes(groups, cropItems);
  if (!missing.length) return;

  const selection = { keys: missing.map(missingKey) };
  const confirmed = await new Promise<boolean>((resolve) => {
    modal.confirm({
      title: '驗證範圍尚未建立於作物管理',
      width: 560,
      content: <MissingScopesChecklist missing={missing} selection={selection} />,
      okText: '新增勾選項並繼續',
      cancelText: '不新增，直接繼續',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
  if (!confirmed || !selection.keys.length) return;

  const entries = missing
    .filter((m) => selection.keys.includes(missingKey(m)))
    .map((m) => ({ mainCategory: m.mainCategory, subCategory: m.subCategory, name: m.name }));
  try {
    const res = await apiClient.post<EnsureTopCropScopesResult>(
      '/api/settings/crops/ensure-top-scopes',
      { entries },
    );
    if (res.created.length) {
      message.success(
        `已新增 ${res.created.length} 項驗證範圍至作物管理：${res.created
          .map((c) => `${c.subCategory}／${c.name}`)
          .join('、')}`,
      );
      onCropsChanged?.();
    }
  } catch (err) {
    // 補建失敗不阻斷案件儲存，僅提示並記錄錯誤
    console.error('[ApplicationDetailsFields] ensure top crop scopes failed', err);
    message.warning('驗證範圍新增至作物管理失敗，案件仍會照常儲存，請稍後至作物管理手動新增');
  }
}
